import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    this.animateAddToCart();

    if (!event.target.closest('.quick-add-modal')) this.#animateFlyToCart();
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    
    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);

    // ========== START UPSELL CODE ==========
    // Check if upsell bundle exists and has checked products
    // Try multiple selectors and wait a bit if needed to ensure we find it
    let upsellBundle = /** @type {HTMLElement | null} */ (document.querySelector('[data-upsell-bundle]'));
    
    // Fallback: try finding by class if data attribute doesn't work
    if (!upsellBundle) {
      upsellBundle = /** @type {HTMLElement | null} */ (document.querySelector('.upsell-bundle'));
    }
    
    // If still not found, try within the form's parent container
    if (!upsellBundle && form) {
      const formParent = form.closest('.product-details, .product-information, section');
      if (formParent) {
        upsellBundle = /** @type {HTMLElement | null} */ (formParent.querySelector('[data-upsell-bundle]'));
      }
    }
    
    if (upsellBundle) {
      const checkedBoxes = upsellBundle.querySelectorAll('.upsell-bundle__product-checkbox:checked');
      console.log('Upsell bundle found with', checkedBoxes.length, 'checked products');
      
      // Verify we have valid variant IDs before proceeding
      /** @type {HTMLInputElement[]} */
      const validCheckedBoxes = [];
      checkedBoxes.forEach((checkbox) => {
        const checkboxEl = /** @type {HTMLInputElement} */ (checkbox);
        const variantId = checkboxEl.dataset?.variantId;
        if (variantId && String(variantId).trim() !== '') {
          validCheckedBoxes.push(checkboxEl);
          console.log('Valid upsell checkbox variant ID:', variantId);
        } else {
          console.warn('Invalid or missing variant ID for checkbox:', checkboxEl);
        }
      });
      
      if (validCheckedBoxes.length > 0) {
        // Stop default behaviour from the browser
        event.preventDefault();
        event.stopPropagation();
        
        if (this.#timeout) clearTimeout(this.#timeout);

        // Check if the add to cart button is disabled and do an early return if it is
        if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;
        
        // Handle upsell bundle add to cart with valid checkboxes only
        this.#handleUpsellAddToCart(event, form, formData, upsellBundle, validCheckedBoxes).catch((error) => {
          console.error('Upsell bundle error:', error);
          // Fallback to normal add to cart if upsell fails
          // Don't submit the form, let the normal flow handle it
          console.log('Falling back to normal add to cart');
        });
        return;
      } else if (checkedBoxes.length > 0) {
        console.warn('Upsell bundle found but no valid variant IDs. Proceeding with normal add to cart.');
      }
    }
    // ========== END UPSELL CODE ==========
    
    // Stop default behaviour from the browser (for normal add to cart)
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          window.dispatchEvent(new CartErrorEvent(this.id, response.message));

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  // ========== START UPSELL CODE ==========
  /**
   * Handle add to cart with upsell products
   * @param {Event} event - Submit event
   * @param {HTMLFormElement} form - Form element
   * @param {FormData} formData - Form data
   * @param {HTMLElement} upsellBundle - Upsell bundle element
   * @param {NodeList | HTMLInputElement[]} checkedBoxes - Checked upsell checkboxes
   */
  async #handleUpsellAddToCart(event, form, formData, upsellBundle, checkedBoxes) {
    const { addToCartTextError } = this.refs;
    const mainVariantId = formData.get('id');
    const quantityValue = formData.get('quantity');
    const quantity = quantityValue ? String(quantityValue) : '1';

    // Get discount code from data attribute if available
    const discountCode = (upsellBundle.dataset && upsellBundle.dataset.discountCode) || '';

    try {
      // Get cart section IDs first
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      /** @type {string[]} */
      const sectionIds = [];
      cartItemsComponents.forEach((component) => {
        if (component instanceof HTMLElement && component.dataset.sectionId) {
          sectionIds.push(component.dataset.sectionId);
        }
      });

      // Add products sequentially to avoid cart API race conditions
      const mainVariantIdStr = String(mainVariantId);
      console.log('Adding main product with variant ID:', mainVariantIdStr);
      
      // Add main product first
      const mainProductResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          id: mainVariantIdStr, 
          quantity: parseInt(quantity) 
        })
      });
      
      if (!mainProductResponse.ok) {
        const error = await mainProductResponse.json();
        console.error('Failed to add main product:', mainVariantIdStr, error);
        throw new Error(error.description || 'Failed to add main product to cart');
      }
      
      const mainProductResult = await mainProductResponse.json();
      console.log('Successfully added main product:', mainVariantIdStr, mainProductResult);
      
      // Now add upsell products sequentially
      /** @type {any[]} */
      const upsellResults = [];

      // Add upsell products sequentially to avoid cart API issues
      // Convert NodeList to Array if needed for better iteration
      const checkedBoxesArray = Array.from(checkedBoxes);
      console.log('Processing', checkedBoxesArray.length, 'checked upsell products');
      
      if (checkedBoxesArray.length === 0) {
        console.warn('No checked boxes found');
      }
      
      // Add each upsell product one by one
      for (let index = 0; index < checkedBoxesArray.length; index++) {
        const checkbox = checkedBoxesArray[index];
        const checkboxElement = /** @type {HTMLInputElement} */ (checkbox);
        
        // Try multiple ways to get variant ID
        let variantId = '';
        if (checkboxElement.dataset && checkboxElement.dataset.variantId) {
          variantId = checkboxElement.dataset.variantId;
        } else if (checkboxElement.getAttribute && checkboxElement.getAttribute('data-variant-id')) {
          variantId = checkboxElement.getAttribute('data-variant-id') || '';
        }
        
        // Also try getting from parent element
        if (!variantId || variantId.trim() === '') {
          const productElement = /** @type {HTMLElement | null} */ (checkboxElement.closest('.upsell-bundle__product'));
          if (productElement) {
            if (productElement.dataset && productElement.dataset.variantId) {
              variantId = productElement.dataset.variantId;
            } else if (productElement.getAttribute && productElement.getAttribute('data-variant-id')) {
              variantId = productElement.getAttribute('data-variant-id') || '';
            }
          }
        }
        
        const productElement = /** @type {HTMLElement | null} */ (checkboxElement.closest('.upsell-bundle__product'));
        const originalPriceElement = productElement ? productElement.querySelector('.upsell-bundle__product-original-price') : null;
        const originalPrice = originalPriceElement && originalPriceElement.textContent ? originalPriceElement.textContent.trim() : '';
        
        if (variantId && variantId.trim() !== '') {
          // Convert variant ID to string (Shopify API expects string)
          const variantIdStr = String(variantId).trim();
          console.log('Adding upsell product', index + 1, 'of', checkedBoxesArray.length, 'with variant ID:', variantIdStr);
          
          try {
            const upsellResponse = await fetch('/cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                id: variantIdStr,
                quantity: 1,
                properties: {
                  '_upsell': 'true',
                  '_original_price': originalPrice,
                  '_free_gift': 'yes'
                }
              })
            });
            
            if (!upsellResponse.ok) {
              const error = await upsellResponse.json();
              console.error('Failed to add upsell product:', variantIdStr, error);
              // Continue with other products even if one fails
              continue;
            }
            
            const upsellResult = await upsellResponse.json();
            console.log('Successfully added upsell product:', variantIdStr, upsellResult);
            upsellResults.push(upsellResult);
            
            // Small delay between requests to avoid overwhelming the API
            if (index < checkedBoxesArray.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          } catch (error) {
            console.error('Error adding upsell product:', variantIdStr, error);
            // Continue with other products even if one fails
          }
        } else {
          console.error('No variant ID found for checkbox at index', index, ':', checkboxElement);
          // Log the checkbox element to debug
          console.error('Checkbox element:', checkboxElement);
          console.error('Checkbox dataset:', checkboxElement.dataset);
          console.error('Checkbox attributes:', {
            'data-variant-id': checkboxElement.getAttribute('data-variant-id'),
            'data-product-id': checkboxElement.getAttribute('data-product-id'),
            checked: checkboxElement.checked
          });
          console.error('Parent product element:', productElement);
          if (productElement) {
            console.error('Parent dataset:', productElement.dataset);
            console.error('Parent attributes:', {
              'data-variant-id': productElement.getAttribute('data-variant-id'),
              'data-product-id': productElement.getAttribute('data-product-id')
            });
          }
        }
      }
      
      console.log('Successfully added', upsellResults.length, 'out of', checkedBoxesArray.length, 'upsell products');

      // Hide any error messages
      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      // Add aria-live region to inform screen readers that the item was added
      if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
        const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
        const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
        const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

        this.#setLiveRegionText(addedText);

        setTimeout(() => {
          this.#clearLiveRegionText();
        }, 5000);
      }

      // Fetch cart and sections in PARALLEL for maximum speed
      const [cartResponse, sections] = await Promise.all([
        fetch('/cart.js').then(r => r.json()),
        sectionIds.length > 0 ? this.#fetchSections(sectionIds) : Promise.resolve({})
      ]);
      
      let cart = cartResponse;

      // Apply discount code if available (and get updated sections if possible)
      if (discountCode && sectionIds.length > 0) {
        try {
          const discountResponse = await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              discount: discountCode,
              sections: sectionIds
            })
          });
          
          if (discountResponse.ok) {
            const discountData = await discountResponse.json();
            // Update sections if discount response includes them
            if (discountData.sections && Object.keys(discountData.sections).length > 0) {
              Object.assign(sections, discountData.sections);
            }
            // Update cart with discount applied
            if (discountData.item_count !== undefined) {
              cart = discountData;
            }
          }
        } catch (discountError) {
          console.warn('Discount code could not be applied:', discountError);
        }
      }

      // Dispatch cart add event with ALL products and sections immediately
      this.dispatchEvent(
        new CartAddEvent(cart, this.id, {
          source: 'product-form-component',
          itemCount: cart.item_count || 0,
          productId: this.dataset.productId,
          variantId: String(mainVariantId || ''),
          sections: sections,
        })
      );

      // Open cart drawer IMMEDIATELY with ALL products showing together!
      const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      // If sections are empty, fetch them in background and update (non-blocking)
      if (Object.keys(sections).length === 0 && sectionIds.length > 0) {
        // Fetch sections in background and update drawer
        this.#fetchAndUpdateSectionsInBackground(sectionIds, formData);
      }

      // Show success message
      this.#showUpsellSuccessMessage();

      cartPerformance.measureFromEvent('add:user-action', event);

    } catch (error) {
      console.error('Error adding items to cart:', error);
      
      if (addToCartTextError) {
        addToCartTextError.classList.remove('hidden');
        const textNode = addToCartTextError.childNodes[2];
        if (textNode) {
          textNode.textContent = error.message || 'There was an error adding items to your cart. Please try again.';
        } else {
          const newTextNode = document.createTextNode(error.message || 'There was an error adding items to your cart. Please try again.');
          addToCartTextError.appendChild(newTextNode);
        }
        this.#setLiveRegionText(error.message || 'There was an error adding items to your cart.');
      }
    }
  }

  /**
   * Show success message for upsell bundle
   */
  #showUpsellSuccessMessage() {
    const message = document.createElement('div');
    message.className = 'upsell-bundle__success-message';
    message.textContent = 'Added to cart successfully!';
    
    document.body.appendChild(message);
    
    setTimeout(() => {
      message.style.transition = 'opacity 0.3s';
      message.style.opacity = '0';
      setTimeout(() => message.remove(), 300);
    }, 3000);
  }

  /**
   * Add upsell products in background (non-blocking)
   * This updates the drawer after products are added
   * @param {NodeList} checkedBoxes - Checked upsell checkboxes
   * @param {string} discountCode - Discount code to apply
   * @param {string[]} sectionIds - Section IDs to fetch
   * @param {Object} cart - Current cart object
   * @param {FormData} formData - Form data to get variant ID
   */
  async #addUpsellProductsInBackground(checkedBoxes, discountCode, sectionIds, cart, formData) {
    // Add upsell products one by one
    /** @type {Promise<Response>[]} */
    const upsellPromises = [];
    checkedBoxes.forEach(function(checkbox) {
      const checkboxElement = /** @type {HTMLInputElement} */ (checkbox);
      const variantId = (checkboxElement.dataset && checkboxElement.dataset.variantId) || '';
      const productElement = /** @type {HTMLElement | null} */ (checkboxElement.closest('.upsell-bundle__product'));
      const originalPriceElement = productElement ? productElement.querySelector('.upsell-bundle__product-original-price') : null;
      const originalPrice = originalPriceElement && originalPriceElement.textContent ? originalPriceElement.textContent.trim() : '';
      
      if (variantId) {
        const upsellPromise = fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: {
              '_upsell': 'true',
              '_original_price': originalPrice,
              '_free_gift': 'yes'
            }
          })
        });
        upsellPromises.push(upsellPromise);
      }
    });

    // Wait for all upsell products to be added (in background)
    try {
      await Promise.all(upsellPromises);
      
      // ALWAYS fetch sections after upsell products are added to update cart drawer
      await this.#applyDiscountAndFetchSectionsAsync(discountCode, sectionIds, formData);
    } catch (error) {
      console.warn('Error adding upsell products in background:', error);
      // Even if there's an error, try to fetch sections to update the drawer
      if (sectionIds.length > 0) {
        await this.#fetchAndUpdateSections(sectionIds, formData);
      }
    }
  }

  /**
   * Apply discount code and fetch sections asynchronously (non-blocking)
   * This updates the cart drawer content in the background
   * @param {string} discountCode - Discount code to apply
   * @param {string[]} sectionIds - Section IDs to fetch
   * @param {FormData} formData - Form data to get variant ID
   */
  async #applyDiscountAndFetchSectionsAsync(discountCode, sectionIds, formData) {
    /** @type {Record<string, string>} */
    let sections = {};
    /** @type {any} */
    let finalCart = null;

    // Apply discount code if available and get sections
    if (discountCode && sectionIds.length > 0) {
      try {
        const discountResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            discount: discountCode,
            sections: sectionIds
          })
        });
        
        if (discountResponse.ok) {
          const discountData = await discountResponse.json();
          finalCart = discountData;
          if (discountData.sections) {
            sections = discountData.sections;
          }
        }
      } catch (discountError) {
        console.warn('Discount code could not be applied:', discountError);
      }
    }

    // ALWAYS fetch sections if we don't have them yet (even without discount)
    if (Object.keys(sections).length === 0 && sectionIds.length > 0) {
      sections = await this.#fetchSections(sectionIds);
    }

    // ALWAYS dispatch event with sections to update cart drawer (even if empty, cart-items-component will fetch)
    await this.#dispatchCartUpdateWithSections(sections, sectionIds, formData);
  }

  /**
   * Fetch sections using section rendering API
   * @param {string[]} sectionIds - Section IDs to fetch
   * @returns {Promise<Record<string, string>>} Sections object
   */
  async #fetchSections(sectionIds) {
    /** @type {Record<string, string>} */
    const sections = {};
    
    if (sectionIds.length === 0) {
      return sections;
    }

    try {
      // Try fastest approach first: /cart route (cart drawer sections are usually rendered there)
      const url = new URL('/cart', window.location.origin);
      url.searchParams.set('sections', sectionIds.join(','));
      
      const sectionResponse = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (sectionResponse.ok) {
        const sectionData = await sectionResponse.json();
        if (sectionData.sections && Object.keys(sectionData.sections).length > 0) {
          return sectionData.sections;
        }
      }
      
      // Fallback: Try current page URL (only if first approach fails)
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.set('sections', sectionIds.join(','));
      
      const fallbackResponse = await fetch(currentUrl.toString(), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });
      
      if (fallbackResponse.ok) {
        const sectionData = await fallbackResponse.json();
        if (sectionData.sections && Object.keys(sectionData.sections).length > 0) {
          return sectionData.sections;
        }
      }
      
    } catch (sectionError) {
      console.warn('Failed to fetch sections:', sectionError);
    }

    return sections;
  }

  /**
   * Fetch and update sections (fallback method)
   * @param {string[]} sectionIds - Section IDs to fetch
   * @param {FormData} formData - Form data
   */
  async #fetchAndUpdateSections(sectionIds, formData) {
    const sections = await this.#fetchSections(sectionIds);
    await this.#dispatchCartUpdateWithSections(sections, sectionIds, formData);
  }

  /**
   * Fetch and update sections in background (non-blocking)
   * @param {string[]} sectionIds - Section IDs to fetch
   * @param {FormData} formData - Form data
   */
  async #fetchAndUpdateSectionsInBackground(sectionIds, formData) {
    // Small delay to let Shopify process cart updates
    await new Promise(resolve => setTimeout(resolve, 50));
    const sections = await this.#fetchSections(sectionIds);
    if (Object.keys(sections).length > 0) {
      await this.#dispatchCartUpdateWithSections(sections, sectionIds, formData);
    }
  }

  /**
   * Dispatch cart update event with sections
   * @param {Record<string, string>} sections - Sections object
   * @param {string[]} sectionIds - Section IDs
   * @param {FormData} formData - Form data
   */
  async #dispatchCartUpdateWithSections(sections, sectionIds, formData) {
    // Always fetch latest cart to ensure we have accurate data
    let finalCart = null;
    try {
      const latestCartResponse = await fetch('/cart.js');
      finalCart = await latestCartResponse.json();
    } catch (e) {
      console.warn('Failed to fetch latest cart:', e);
      return; // Can't update without cart data
    }

    // Dispatch update event with sections to refresh cart drawer content
    // Even if sections is empty, cart-items-component will fetch them automatically
    const variantId = formData ? String(formData.get('id') || '') : '';
    this.dispatchEvent(
      new CartAddEvent(finalCart, this.id, {
        source: 'product-form-component',
        itemCount: finalCart.item_count || 0,
        productId: this.dataset.productId,
        variantId: variantId,
        sections: sections,
      })
    );
  }
  // ========== END UPSELL CODE ==========

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
